<template>
  <div>
    <div v-if="loading" class="text-center p-4">
      <i class="fas fa-spinner fa-spin fa-2x"></i>
      <p class="mt-2">Carregando checklist...</p>
    </div>

    <div v-else-if="checklistTemplate && checklistTemplate.nome">
      <div class="content-wrapper">

        <div class="page-header">
          <ol class="breadcrumb">
            <li class="breadcrumb-item">
              <router-link to="/checklists">Executar Checklists</router-link>
            </li>
            <li class="breadcrumb-item active">Visualizar</li>
          </ol>
          <h2 class="page-title">{{ checklistTemplate.nome }}</h2>
        </div>

        <div class="checklist-container">

          <div v-if="expirado" class="alert alert-danger text-center mt-4">
            <i class="fas fa-clock mr-2"></i>
            O tempo para preenchimento desta checklist expirou.
          </div>

          <div class="obs-wrapper" v-if="checklistTemplate.informacoes_iniciais">
            <div class="obs-card">
              <h4>Informações iniciais</h4>
              <div class="informacoes-iniciais-conteudo" v-html="checklistTemplate.informacoes_iniciais"></div>
            </div>
          </div>

          <div class="obs-wrapper" v-if="checklistTemplate.checklist_template_id">
            <div class="obs-card">
              <h4>Unidade da Execução</h4>

              <v-select
                v-if="selectableUnits.length > 0"
                v-model="selectedUnitId"
                :options="selectableUnits"
                label="name"
                placeholder="Selecione a unidade"
                :reduce="unit => Number(unit.id)"
                :disabled="expirado || (isEdicao && !!selectedUnitId)"
              >
                <template #no-options>Nenhuma unidade encontrada</template>
              </v-select>

              <div v-if="isRescue" class="alert alert-info mb-0 mt-2">
                A unidade original deste checklist foi removida. Selecione uma unidade do seu grupo para executá-lo.
              </div>

              <div v-if="availableUnits.length === 0 && selectableUnits.length === 0" class="alert alert-warning mb-0">
                Nenhuma unidade disponível no seu grupo; contate um gestor.
              </div>
            </div>
          </div>

          <!-- Group tab bar -->
          <div class="group-tabs-outer" v-if="(checklistTemplate.grupos_checklist || []).length > 0">
            <button type="button" class="tab-arrow" @click="scrollTabs(-1)">
              <i class="fas fa-chevron-left"></i>
            </button>
            <div class="group-tabs-wrapper" ref="tabsWrapper">
              <div class="group-tabs">
                <button
                  v-for="(group, idx) in checklistTemplate.grupos_checklist || []"
                  :key="group.grupo_checklist_id"
                  type="button"
                  class="group-tab-btn"
                  :class="{ active: selectedGroupIndex === idx }"
                  @click="selectedGroupIndex = idx"
                >
                  <span class="tab-title">{{ group.titulo }}</span>
                  <span class="tab-progress">{{ getProgressoGrupo(group) }}</span>
                </button>
              </div>
            </div>
            <button type="button" class="tab-arrow" @click="scrollTabs(1)">
              <i class="fas fa-chevron-right"></i>
            </button>
          </div>

          <div
            v-for="(group, groupIdx) in checklistTemplate.grupos_checklist || []"
            :key="group.grupo_checklist_id"
            v-show="selectedGroupIndex === groupIdx"
            class="group-dark-container"
          >
            <div class="group-card">

              <div class="group-header-lined">
                <div class="group-title-lined">
                  <i class="ft-folder mr-2"></i>
                  {{ group.titulo }}
                </div>

                <div class="group-title-line"></div>

                <span class="group-count">
                  {{ getProgressoGrupo(group) }}
                </span>
              </div>

              <div
                v-for="(item, index) in (group.item_checklist || []).filter(i => !i.is_condicional)"
                :key="item.item_checklist_id"
                class="item-block"
                :class="{ filled: isItemFilled(item) }"
              >
                <label class="item-label">
                  <span class="item-index">{{ index + 1 }}</span>
                  <span class="item-title-text">{{ item.titulo }}<span v-if="!!item.obrigatorio" class="required-star">*</span></span>
                </label>

                <div v-if="item.tipo_pergunta == 5 && item.data_esperada" class="data-esperada-info">
                  <i class="far fa-calendar-check mr-1"></i>
                  <strong>{{ formatarDataEsperada(item.data_esperada) }}</strong>
                </div>

                <input
                  v-if="item.tipo_pergunta == 2"
                  type="text"
                  class="form-control input-modern"
                  placeholder="Digite sua resposta"
                  v-model="item.resposta"
                  :disabled="expirado"
                />

                <b-form-datepicker
                  v-if="item.tipo_pergunta == 5"
                  v-model="item.resposta"
                  class="input-modern input-data"
                  locale="pt-BR"
                  placeholder="Selecione uma data"
                  no-flip
                  :date-format-options="{ day: '2-digit', month: '2-digit', year: 'numeric' }"
                  start-weekday="0"
                  today-button
                  reset-button
                  close-button
                  label-today-button="Hoje"
                  label-reset-button="Limpar"
                  label-close-button="Fechar"
                  label-no-date-selected="Nenhuma data selecionada"
                  label-help="Use as setas do teclado para navegar"
                  label-prev-month="Mês anterior"
                  label-next-month="Próximo mês"
                  label-current-month="Mês atual"
                  label-prev-year="Ano anterior"
                  label-next-year="Próximo ano"
                  :disabled="expirado"
                />

                <input
                  v-if="item.tipo_pergunta == 3"
                  type="number"
                  step="any"
                  class="form-control input-modern"
                  placeholder="0"
                  v-model="item.resposta"
                  @input="normalizarNumero($event, item)"
                  :disabled="expirado"
                />
                <small
                  v-if="item.tipo_pergunta == 3 && isRespostaZero(item)"
                  class="text-warning d-block mt-1"
                >
                  <i class="fas fa-exclamation-triangle mr-1"></i>
                  Atenção: valor preenchido como zero
                </small>

                <!-- Multiple choice (checkboxes) -->
                <div v-if="item.tipo_pergunta == 1 && item.multipla_escolha" class="multipla-escolha-wrapper">
                  <div
                    v-for="resp in getRespostasDoItem(item.item_checklist_id)"
                    :key="resp.resposta_checklist_id"
                    class="multipla-opcao"
                  >
                    <label class="multipla-label">
                      <input
                        type="checkbox"
                        :value="resp.resposta_checklist_id"
                        v-model="item.resposta"
                        :disabled="expirado"
                      />
                      <span class="opcao-texto">{{ resp.titulo }}</span>
                    </label>
                    <!-- Linked conditional item -->
                    <div
                      v-if="resp.item_vinculado && Array.isArray(item.resposta) && item.resposta.includes(resp.resposta_checklist_id)"
                      class="item-vinculado-block"
                    >
                      <item-execucao :item="resp.item_vinculado" :expirado="expirado" />
                    </div>
                  </div>
                </div>

                <!-- Single choice (select) -->
                <template v-if="item.tipo_pergunta == 1 && !item.multipla_escolha">
                  <select
                    class="form-control input-modern"
                    :value="item.resposta"
                    :disabled="expirado"
                    @change="onRespostaChange(item, $event)"
                  >
                    <option value="">Selecione uma opção</option>
                    <option
                      v-for="resp in getRespostasDoItem(item.item_checklist_id)"
                      :key="resp.resposta_checklist_id"
                      :value="resp.resposta_checklist_id"
                    >
                      {{ resp.titulo }}
                    </option>
                  </select>
                  <!-- Linked conditional item for single choice -->
                  <div
                    v-if="item._linkedItem"
                    class="item-vinculado-block"
                  >
                    <item-execucao :item="item._linkedItem" :expirado="expirado" />
                  </div>
                </template>

                <div v-if="item.tipo_pergunta == 4" class="image-upload-wrapper">
                  <input
                    type="file"
                    accept="image/*"
                    class="d-none"
                    :ref="'fileInput' + item.item_checklist_id"
                    @change="onImagemSelecionada($event, item)"
                    :disabled="expirado"
                  />

                  <button
                    type="button"
                    class="btn btn-outline-primary btn-upload"
                    @click="triggerFileInput(item.item_checklist_id)"
                    :disabled="expirado"
                  >
                    <i class="fas fa-camera mr-2"></i>
                    {{ item.arquivo ? 'Alterar Foto' : 'Escolher Foto' }}
                  </button>

                  <div v-if="item.arquivo" class="file-info mt-2">
                    <i class="fas fa-file-image text-success mr-1"></i>
                    <span class="file-name">{{ item.arquivo.name }}</span>
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-danger ml-2"
                      @click="removerImagem(item)"
                      :disabled="expirado"
                    >
                      <i class="fas fa-times"></i>
                    </button>
                  </div>

                  <div v-if="item.preview" class="preview-container mt-3">
                    <img :src="item.preview" class="photo" alt="Pré-visualização da imagem" />
                  </div>
                </div>

              </div>
            </div>

            <div class="group-nav-footer">
              <button
                v-if="selectedGroupIndex > 0"
                type="button"
                class="btn btn-outline-secondary btn-nav-grupo"
                @click="anterior()"
              >
                <i class="fas fa-chevron-left mr-2"></i>
                Grupo anterior
              </button>
              <button
                v-if="selectedGroupIndex < (checklistTemplate.grupos_checklist || []).length - 1"
                type="button"
                class="btn btn-primary btn-nav-grupo"
                @click="proximo()"
              >
                Próximo grupo
                <i class="fas fa-chevron-right ml-2"></i>
              </button>
            </div>
          </div>

          <div class="obs-wrapper">
            <div class="obs-card">
              <h4>Observações</h4>
              <textarea
                class="form-control textarea-modern"
                rows="4"
                v-model="observacao"
                placeholder="Observações gerais (opcional)"
                :disabled="expirado"
              ></textarea>
            </div>
          </div>

          <div class="action-footer">
            <button class="btn btn-secondary btn-lg mr-3" @click="salvarChecklist(false)" :disabled="salvando || expirado || !selectedUnitId">
              <i class="ft-save" v-if="!salvando"></i>
              <i class="fa fa-spinner fa-spin" v-else></i>
              {{ salvando ? 'Salvando…' : 'Salvar Parcialmente' }}
            </button>

            <button class="btn btn-primary btn-lg" @click="salvarChecklist(true)" :disabled="salvando || expirado || !selectedUnitId">
              <i class="ft-save" v-if="!salvando"></i>
              <i class="fa fa-spinner fa-spin" v-else></i>
              {{ salvando ? 'Salvando…' : 'Finalizar Checklist' }}
            </button>
          </div>

        </div>
      </div>
    </div>

    <div v-else class="alert alert-danger m-4">
      <i class="fas fa-exclamation-triangle"></i>
      Erro ao carregar checklist ou checklist não encontrado.
    </div>
  </div>
</template>

<script>
import { BFormDatepicker } from "bootstrap-vue";
import ItemExecucao from "./ItemExecucao.vue";

export default {
  name: "ChecklistView",
  components: { ItemExecucao, BFormDatepicker },

  data() {
    return {
      checklistTemplate: {
        grupos_checklist: [],
        units: [],
      },
      loading: true,
      observacao: "",
      salvando: false,
      executionId: null,
      isEdicao: false,
      selectedUnitId: null,
      scheduleExecutionId: null,
      expirado: false,
      isAvulso: false,
      selectedGroupIndex: 0,
      groupUnits: [],
    };
  },

  computed: {
    availableUnits() {
      if (!Array.isArray(this.checklistTemplate.units)) return [];
      return [...this.checklistTemplate.units].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { numeric: true })
      );
    },
    selectableUnits() {
      if (this.availableUnits.length > 0) return this.availableUnits;
      if (!Array.isArray(this.groupUnits)) return [];
      return [...this.groupUnits].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { numeric: true })
      );
    },
    isRescue() {
      return this.availableUnits.length === 0 && this.selectableUnits.length > 0;
    },
  },

  watch: {
    selectableUnits(units) {
      if (
        this.selectedUnitId &&
        !units.some(u => Number(u.id) === Number(this.selectedUnitId))
      ) {
        this.selectedUnitId = null;
      }
    },
    selectedUnitId(val) {
      if (
        val &&
        !this.selectableUnits.some(u => Number(u.id) === Number(val))
      ) {
        this.selectedUnitId = null;
      }
    },
    selectedGroupIndex(idx) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      this.$nextTick(() => this.scrollTabIntoView(idx));
    },
  },

  beforeMount() {
    this.init();
  },

  methods: {
    // data_esperada vem como "AAAA-MM-DD"; formata sem passar por Date para não mudar o dia por fuso
    formatarDataEsperada(data) {
      const [ano, mes, dia] = String(data).substring(0, 10).split("-");
      return dia && mes && ano ? `${dia}/${mes}/${ano}` : data;
    },


    async init() {
      try {
        const isRetomar = this.$route.name === 'retomar-checklist-execution';
        this.isAvulso = this.$route.query.avulso === '1';

        if (isRetomar) {
          await this.initRetomar(this.$route.params.id);
        } else if (this.isAvulso) {
          await this.initAvulso(this.$route.params.id);
        } else {
          await this.initAgendado(this.$route.params.id);
        }

        if (this.availableUnits.length === 0) {
          await this.loadGroupUnits();
        }
      } catch (error) {
        console.error("Erro ao carregar checklist:", error?.response || error);
        this.$message("Erro", "Erro ao carregar checklist", "error");
      } finally {
        this.loading = false;
      }
    },

    async loadGroupUnits() {
      try {
        const response = await this.$http.get('me/group-units');
        const data = response.data && response.data.data !== undefined
          ? response.data.data
          : response.data;
        this.groupUnits = Array.isArray(data) ? data : [];
      } catch (error) {
        this.groupUnits = [];
      }
    },

    async initRetomar(executionId) {
      const response = await this.$http.get(`checklistexecutions/${executionId}?with[]=respostas`);
      const execution = Array.isArray(response.data.data)
        ? response.data.data[0]
        : response.data.data;

      if (!execution) {
        this.$message("Erro", "Execução não encontrada", "error");
        return;
      }

      const currentUserId = Number(this.$store.state.user.usuario_id);
      if (Number(execution.usuario_id) !== currentUserId) {
        this.$message("Erro", "Você não pode retomar a execução de outro usuário", "error");
        this.$router.push('/checklist-executions');
        return;
      }

      if (execution.status !== 4) {
        this.$message("Erro", "Esta execução não pode ser retomada", "error");
        this.$router.push('/checklist-executions');
        return;
      }

      await this.initAvulso(execution.checklist_template_id);

      this.selectedUnitId = execution.unit_id ? Number(execution.unit_id) : null;
      this.observacao = execution.observacao || "";
      this.executionId = Number(executionId);
      this.isEdicao = true;

      if (execution.respostas && execution.respostas.length > 0) {
        execution.respostas.forEach(resposta => {
          const item = this.encontrarItemPorId(resposta.item_checklist_id);
          if (item) {
            if (resposta.valor_imagem) {
              this.$set(item, 'preview', '/storage/' + resposta.valor_imagem);
              this.$set(item, 'resposta', "__IMAGEM__");
            } else if (resposta.valor_numero !== null && resposta.valor_numero !== undefined) {
              this.$set(item, 'resposta', resposta.valor_numero);
            } else if (resposta.valor_texto) {
              this.$set(item, 'resposta', resposta.valor_texto);
            } else if (resposta.resposta_checklist_id) {
              if (item.multipla_escolha) {
                if (!Array.isArray(item.resposta)) this.$set(item, 'resposta', []);
                item.resposta.push(resposta.resposta_checklist_id);
              } else {
                this.$set(item, 'resposta', resposta.resposta_checklist_id);
              }
            }
          }
        });
      }
      this.rebuildLinkedItemsMap();
    },

    async initAvulso(templateId) {
      const response = await this.$http.get(
        `checklisttemplates/${templateId}?with[]=units&with[]=grupos_checklist&with[]=grupos_checklist.item_checklist&with[]=grupos_checklist.item_checklist.itens&with[]=grupos_checklist.item_checklist.itens.item_vinculado`
      );

      const template = Array.isArray(response.data.data)
        ? response.data.data[0]
        : (response.data.data || response.data);

      if (!template || !template.nome) {
        this.$message("Erro", "Checklist não encontrado", "error");
        return;
      }

      this.checklistTemplate = template;

      if (this.checklistTemplate.grupos_checklist) {
        this.checklistTemplate.grupos_checklist.sort((a, b) => {
          const oa = a.pivot?.ordem !== null && a.pivot?.ordem !== undefined ? a.pivot.ordem : 999999;
          const ob = b.pivot?.ordem !== null && b.pivot?.ordem !== undefined ? b.pivot.ordem : 999999;
          return oa !== ob ? oa - ob : parseInt(a.grupo_checklist_id) - parseInt(b.grupo_checklist_id);
        });

        this.checklistTemplate.grupos_checklist.forEach(group => {
          if (group.item_checklist && Array.isArray(group.item_checklist)) {
            group.item_checklist.sort((a, b) => {
              const oa = a.ordem !== null && a.ordem !== undefined ? a.ordem : 999999;
              const ob = b.ordem !== null && b.ordem !== undefined ? b.ordem : 999999;
              return oa !== ob ? oa - ob : parseInt(a.item_checklist_id) - parseInt(b.item_checklist_id);
            });
          }
        });
      }

      if (this.checklistTemplate.units && this.checklistTemplate.units.length === 1) {
        this.selectedUnitId = Number(this.checklistTemplate.units[0].id);
      }

      this.scheduleExecutionId = null;
      this.initializeRespostas();
    },

    async initAgendado(scheduleExecutionId) {
      const response = await this.$http.get(
        `checklistscheduleexecutions/${scheduleExecutionId}?with[]=checklist_template&with[]=checklist_template.units&with[]=checklist_template.grupos_checklist&with[]=checklist_template.grupos_checklist.item_checklist&with[]=checklist_template.grupos_checklist.item_checklist.itens&with[]=checklist_template.grupos_checklist.item_checklist.itens.item_vinculado&with[]=checklist_execution&with[]=checklist_execution.respostas`
      );

      const scheduleExecution = Array.isArray(response.data.data)
        ? response.data.data[0]
        : response.data.data;

      if (!scheduleExecution || !scheduleExecution.checklist_template) {
        this.$message("Erro", "Checklist não encontrada", "error");
        return;
      }

      const agora = new Date();
      const fim = new Date(scheduleExecution.data_hora_fim);

      if (fim < agora) {
        this.expirado = true;
      }

      this.checklistTemplate = scheduleExecution.checklist_template;

      if (this.checklistTemplate.grupos_checklist) {
        this.checklistTemplate.grupos_checklist.sort((a, b) => {
          const oa = a.pivot?.ordem !== null && a.pivot?.ordem !== undefined ? a.pivot.ordem : 999999;
          const ob = b.pivot?.ordem !== null && b.pivot?.ordem !== undefined ? b.pivot.ordem : 999999;
          return oa !== ob ? oa - ob : parseInt(a.grupo_checklist_id) - parseInt(b.grupo_checklist_id);
        });

        this.checklistTemplate.grupos_checklist.forEach(group => {
          if (group.item_checklist && Array.isArray(group.item_checklist)) {
            group.item_checklist.sort((a, b) => {
              const oa = a.ordem !== null && a.ordem !== undefined ? a.ordem : 999999;
              const ob = b.ordem !== null && b.ordem !== undefined ? b.ordem : 999999;
              return oa !== ob ? oa - ob : parseInt(a.item_checklist_id) - parseInt(b.item_checklist_id);
            });
          }
        });
      }

      this.selectedUnitId = scheduleExecution.unit_id ? Number(scheduleExecution.unit_id) : null;
      this.scheduleExecutionId = scheduleExecution.checklist_schedule_execution_id;

      this.initializeRespostas();

      if (scheduleExecution.checklist_execution_id && scheduleExecution.checklist_execution) {
        this.executionId = scheduleExecution.checklist_execution_id;
        this.isEdicao = true;
        this.observacao = scheduleExecution.checklist_execution.observacao || "";

        if (scheduleExecution.checklist_execution.respostas && scheduleExecution.checklist_execution.respostas.length > 0) {
          scheduleExecution.checklist_execution.respostas.forEach(resposta => {
            const item = this.encontrarItemPorId(resposta.item_checklist_id);
            if (item) {
              if (resposta.valor_imagem) {
                this.$set(item, 'preview', '/storage/' + resposta.valor_imagem);
                this.$set(item, 'resposta', "__IMAGEM__");
              } else if (resposta.valor_numero !== null && resposta.valor_numero !== undefined) {
                this.$set(item, 'resposta', resposta.valor_numero);
              } else if (resposta.valor_texto) {
                this.$set(item, 'resposta', resposta.valor_texto);
              } else if (resposta.resposta_checklist_id) {
                if (item.multipla_escolha) {
                  if (!Array.isArray(item.resposta)) this.$set(item, 'resposta', []);
                  item.resposta.push(resposta.resposta_checklist_id);
                } else {
                  this.$set(item, 'resposta', resposta.resposta_checklist_id);
                }
              }
            }
          });
        }
        this.rebuildLinkedItemsMap();
      }
    },
    
    async carregarExecucaoExistente(executionId) {

      const response = await this.$http.get(`checklistexecutions/${executionId}`);

      if (response.data && response.data.data) {

        const execution = Array.isArray(response.data.data)
          ? response.data.data[0]
          : response.data.data;

        if (!execution || !execution.checklist_template) {
          console.error('Execução inválida:', execution);
          this.$message("Erro", "Erro ao carregar execução", "error");
          return;
        }

        this.checklistTemplate = execution.checklist_template;
        
        // Ordenar grupos e itens pela ordem definida no template
        if (this.checklistTemplate.grupos_checklist) {
          this.checklistTemplate.grupos_checklist.sort((a, b) => {
            const oa = a.pivot?.ordem !== null && a.pivot?.ordem !== undefined ? a.pivot.ordem : 999999;
            const ob = b.pivot?.ordem !== null && b.pivot?.ordem !== undefined ? b.pivot.ordem : 999999;
            return oa !== ob ? oa - ob : parseInt(a.grupo_checklist_id) - parseInt(b.grupo_checklist_id);
          });

          this.checklistTemplate.grupos_checklist.forEach(group => {
            if (group.item_checklist && Array.isArray(group.item_checklist)) {
              group.item_checklist.sort((a, b) => {
                const oa = a.ordem !== null && a.ordem !== undefined ? a.ordem : 999999;
                const ob = b.ordem !== null && b.ordem !== undefined ? b.ordem : 999999;
                return oa !== ob ? oa - ob : parseInt(a.item_checklist_id) - parseInt(b.item_checklist_id);
              });
            }
          });
        }
        
        this.observacao = execution.observacao || "";
        this.selectedUnitId = execution.unit_id ? Number(execution.unit_id) : null;

        this.initializeRespostas();

        if (execution.respostas && execution.respostas.length > 0) {
          execution.respostas.forEach(resposta => {
            const item = this.encontrarItemPorId(resposta.item_checklist_id);
            if (item) {

              if (resposta.valor_imagem) {
                this.$set(item, 'preview', '/storage/' + resposta.valor_imagem);
                this.$set(item, 'resposta', "__IMAGEM__");
              } else if (resposta.valor_numero !== null && resposta.valor_numero !== undefined) {
                this.$set(item, 'resposta', resposta.valor_numero);
              } else if (resposta.valor_texto) {
                this.$set(item, 'resposta', resposta.valor_texto);
              } else if (resposta.resposta_checklist_id) {
                if (item.multipla_escolha) {
                  if (!Array.isArray(item.resposta)) this.$set(item, 'resposta', []);
                  item.resposta.push(resposta.resposta_checklist_id);
                } else {
                  this.$set(item, 'resposta', resposta.resposta_checklist_id);
                }
              }

            }
          });
        }
        this.rebuildLinkedItemsMap();
      }
    },

    isItemFilled(item) {
      if (item.tipo_pergunta == 4) {
        return !!item.arquivo || !!item.preview || item.resposta === "__IMAGEM__";
      }
      if (Array.isArray(item.resposta)) {
        return item.resposta.length > 0;
      }
      return item.resposta !== null && item.resposta !== undefined && item.resposta.toString().trim() !== '';
    },

    encontrarItemPorId(itemId) {
      if (!this.checklistTemplate.grupos_checklist) return null;

      const searchInItem = (item) => {
        if (String(item.item_checklist_id) === String(itemId)) return item;
        if (item.itens) {
          for (const resp of item.itens) {
            if (resp.item_vinculado) {
              const found = searchInItem(resp.item_vinculado);
              if (found) return found;
            }
          }
        }
        return null;
      };

      for (let grupo of this.checklistTemplate.grupos_checklist) {
        if (grupo.item_checklist) {
          for (let item of grupo.item_checklist) {
            const found = searchInItem(item);
            if (found) return found;
          }
        }
      }
      return null;
    },

    initializeRespostas() {
      if (this.checklistTemplate && this.checklistTemplate.grupos_checklist) {
        this.checklistTemplate.grupos_checklist.forEach(group => {
          if (group && group.item_checklist) {
            group.item_checklist.forEach(item => {
              if (item) {
                this.$set(item, "resposta", item.multipla_escolha ? [] : "");
                this.$set(item, "arquivo", null);
                this.$set(item, "preview", null);
                this.$set(item, "_linkedItem", null);
              }
            });
          }
        });
      }
    },

    onRespostaChange(item, event) {
      const raw = event.target.value;
      const val = raw === '' ? '' : (isNaN(Number(raw)) ? raw : Number(raw));
      this.$set(item, 'resposta', val);
      const itens = item.itens || [];
      const resp = itens.find(r => String(r.resposta_checklist_id) === String(val));
      const linked = (resp && resp.item_vinculado) ? resp.item_vinculado : null;
      if (linked && linked.resposta === undefined) {
        this.$set(linked, 'resposta', linked.multipla_escolha ? [] : '');
      }
      this.$set(item, '_linkedItem', linked);
    },

    rebuildLinkedItemsMap() {
      if (!this.checklistTemplate || !this.checklistTemplate.grupos_checklist) return;
      this.checklistTemplate.grupos_checklist.forEach(group => {
        if (!group.item_checklist) return;
        group.item_checklist
          .filter(i => !i.is_condicional && i.tipo_pergunta == 1 && !i.multipla_escolha)
          .forEach(item => {
            if (!item.resposta) return;
            const itens = item.itens || [];
            const resp = itens.find(r => String(r.resposta_checklist_id) === String(item.resposta));
            const linked = (resp && resp.item_vinculado) ? resp.item_vinculado : null;
            if (linked && linked.resposta === undefined) {
              this.$set(linked, 'resposta', linked.multipla_escolha ? [] : '');
            }
            this.$set(item, '_linkedItem', linked);
          });
      });
    },

    normalizarNumero(event, item) {
      let valor = event.target.value;
      if (valor) {
        valor = valor.replace(/\./g, '');
        valor = valor.replace(',', '.');
        item.resposta = valor;
        event.target.value = valor;
      }
    },

    isRespostaZero(item) {
      if (item.resposta === null || item.resposta === undefined || item.resposta === '') {
        return false;
      }
      return Number(item.resposta) === 0;
    },

    getProgressoGrupo(grupo) {
      if (!grupo.item_checklist || grupo.item_checklist.length === 0) {
        return "0 itens";
      }

      const itensVisiveis = grupo.item_checklist.filter(i => !i.is_condicional);
      const total = itensVisiveis.length;

      const preenchidos = itensVisiveis.filter(item => {
        return this.isItemFilled(item);
      }).length;

      const porcentagem = Math.round((preenchidos / total) * 100);

      return `${preenchidos}/${total} (${porcentagem}%)`;
    },

    getRespostasDoItem(itemId) {
      if (!this.checklistTemplate.grupos_checklist) return [];

      for (let g = 0; g < this.checklistTemplate.grupos_checklist.length; g++) {
        const group = this.checklistTemplate.grupos_checklist[g];
        if (!group.item_checklist) continue;

        for (let i = 0; i < group.item_checklist.length; i++) {
          const item = group.item_checklist[i];
          if (String(item.item_checklist_id) === String(itemId)) {
            return item.itens || [];
          }
        }
      }
      return [];
    },

    getCurrentLinkedItem(item) {
      if (item.resposta === '' || item.resposta === null || item.resposta === undefined) return null;
      const respostas = this.getRespostasDoItem(item.item_checklist_id);
      const resp = respostas.find(r => String(r.resposta_checklist_id) === String(item.resposta));
      return resp && resp.item_vinculado ? resp.item_vinculado : null;
    },

    onImagemSelecionada(event, item) {
      if (this.expirado) return;

      const file = event.target.files[0];

      if (!file) return;

      if (!file.type.startsWith("image/")) {
        this.$message("Erro", "O arquivo deve ser uma imagem", "error");
        event.target.value = "";
        return;
      }

      item.arquivo = file;
      item.preview = URL.createObjectURL(file);
      item.resposta = "__IMAGEM__";
    },

    triggerFileInput(itemId) {
      if (this.expirado) return;

      const fileInput = this.$refs['fileInput' + itemId];
      if (fileInput && fileInput.length > 0) {
        fileInput[0].click();
      }
    },

    removerImagem(item) {
      if (this.expirado) return;

      item.arquivo = null;
      item.preview = null;
      item.resposta = "";

      const fileInput = this.$refs['fileInput' + item.item_checklist_id];
      if (fileInput && fileInput.length > 0) {
        fileInput[0].value = "";
      }
    },

    async salvarChecklist(finalizar = true) {
      if (this.expirado) {
        this.$message("Erro", "O tempo para preenchimento desta checklist expirou.", "error");
        return;
      }

      if (finalizar) {
        const validationResult = this.validarCamposObrigatorios();
        if (!validationResult.valid) {
          this.$message("Erro", validationResult.message, "error");
          return;
        }
      } else {
        const validationResult = this.validarPeloMenosUmCampo();
        if (!validationResult.valid) {
          this.$message("Erro", validationResult.message, "error");
          return;
        }
      }

      this.salvando = true;

      try {
        if (!this.selectedUnitId) {
          this.$message("Erro", "Selecione a unidade da execução", "error");
          this.salvando = false;
          return;
        }

        const formData = new FormData();

        formData.append(
          "checklist_template_id",
          this.checklistTemplate.checklist_template_id
        );
        if (this.scheduleExecutionId) {
          formData.append("checklist_schedule_execution_id", this.scheduleExecutionId);
        }
        formData.append("unit_id", this.selectedUnitId);
        formData.append("observacao", this.observacao ?? "");
        formData.append("finalizar", finalizar ? "1" : "0");

        let respostaIndex = 0;

        const visitedItems = new Set();
        const appendItem = (item) => {
          if (visitedItems.has(item.item_checklist_id)) return;
          visitedItems.add(item.item_checklist_id);

          if (item.tipo_pergunta !== 4) {
            const hasResposta = Array.isArray(item.resposta) ? item.resposta.length > 0 : (item.resposta && item.resposta.toString().trim() !== "");
            if (finalizar || hasResposta) {
              formData.append(`respostas[${respostaIndex}][item_checklist_id]`, item.item_checklist_id);
              if (Array.isArray(item.resposta)) {
                item.resposta.forEach((val, idx) => {
                  formData.append(`respostas[${respostaIndex}][resposta][${idx}]`, val);
                });
              } else {
                formData.append(`respostas[${respostaIndex}][resposta]`, item.resposta || "");
              }
              respostaIndex++;
            }
          }

          if (item.tipo_pergunta === 4) {
            if (item.arquivo) {
              formData.append(`fotos[foto_item_${item.item_checklist_id}]`, item.arquivo);
            }
            if (item.arquivo || item.preview || item.resposta === "__IMAGEM__") {
              formData.append(`respostas[${respostaIndex}][item_checklist_id]`, item.item_checklist_id);
              formData.append(`respostas[${respostaIndex}][resposta]`, "__IMAGEM__");
              respostaIndex++;
            }
          }

          // Also append linked conditional items that are visible (answered)
          if (item.itens) {
            item.itens.forEach(resp => {
              if (resp.item_vinculado) {
                const selecionado = Array.isArray(item.resposta)
                  ? item.resposta.includes(resp.resposta_checklist_id)
                  : item.resposta == resp.resposta_checklist_id;
                if (selecionado) {
                  appendItem(resp.item_vinculado);
                }
              }
            });
          }
        };

        this.checklistTemplate.grupos_checklist.forEach(group => {
          if (!group.item_checklist) return;
          group.item_checklist.filter(i => !i.is_condicional).forEach(item => appendItem(item));
        });

        if (this.isEdicao && this.executionId) {
          await this.$http.post(`checklistexecutions/${this.executionId}?_method=PUT`, formData, {
            headers: {
              "Content-Type": "multipart/form-data"
            }
          });
        } else {
          await this.$http.post("checklistexecutions", formData, {
            headers: {
              "Content-Type": "multipart/form-data"
            }
          });
        }

        const mensagem = finalizar ? "Checklist finalizada!" : "Checklist salva parcialmente!";
        this.$message("Sucesso", mensagem, "success");
        this.$router.push("/checklist-executions");
      } catch (error) {
        console.error("Erro ao salvar:", error);
        this.$message("Erro", "Erro ao salvar checklist", "error");
      } finally {
        this.salvando = false;
      }
    },

    scrollTabs(direction) {
      const wrapper = this.$refs.tabsWrapper;
      if (wrapper) {
        wrapper.scrollBy({ left: direction * 220, behavior: 'smooth' });
      }
    },

    scrollTabIntoView(idx) {
      const wrapper = this.$refs.tabsWrapper;
      if (!wrapper) return;
      const tab = wrapper.querySelectorAll('.group-tab-btn')[idx];
      if (!tab) return;
      wrapper.scrollTo({
        left: tab.offsetLeft - wrapper.offsetWidth / 2 + tab.offsetWidth / 2,
        behavior: 'smooth',
      });
    },

    proximo() {
      const total = (this.checklistTemplate.grupos_checklist || []).length;
      if (this.selectedGroupIndex < total - 1) {
        this.selectedGroupIndex++;
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },

    anterior() {
      if (this.selectedGroupIndex > 0) {
        this.selectedGroupIndex--;
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },

    validarPeloMenosUmCampo() {
      if (!this.checklistTemplate.grupos_checklist || this.checklistTemplate.grupos_checklist.length === 0) {
        return { valid: false, message: "Nenhum grupo encontrado neste checklist." };
      }

      let totalItens = 0;
      let itensRespondidos = 0;

      this.checklistTemplate.grupos_checklist.forEach(group => {
        if (group.item_checklist && group.item_checklist.length > 0) {
          group.item_checklist.filter(i => !i.is_condicional).forEach((item) => {
            totalItens++;

            if (item.tipo_pergunta == 4) {
              if (item.arquivo || item.preview || item.resposta === "__IMAGEM__") {
                itensRespondidos++;
              }
            } else {
              if (this.isItemFilled(item)) {
                itensRespondidos++;
              }
            }
          });
        }
      });

      if (totalItens === 0) {
        return { valid: false, message: "Este checklist não possui itens para serem respondidos." };
      }

      if (itensRespondidos === 0) {
        return { valid: false, message: "Você precisa preencher pelo menos um campo para salvar parcialmente." };
      }

      return { valid: true, message: "Validação ok!" };
    },

    validarCamposObrigatorios() {
      if (!this.checklistTemplate.grupos_checklist || this.checklistTemplate.grupos_checklist.length === 0) {
        return { valid: false, message: "Nenhum grupo encontrado neste checklist." };
      }

      let totalItens = 0;
      const gruposPendentes = [];

      this.checklistTemplate.grupos_checklist.forEach(group => {
        if (group.item_checklist && group.item_checklist.length > 0) {
          const itensPendentesGrupo = [];

          group.item_checklist.filter(i => !i.is_condicional).forEach((item, index) => {
            totalItens++;

            // Itens não obrigatórios podem ser ignorados
            if (!item.obrigatorio) {
              return;
            }

            if (item.tipo_pergunta == 4) {
              if (!item.arquivo && !item.preview && item.resposta !== "__IMAGEM__") {
                itensPendentesGrupo.push(`${index + 1}. ${item.titulo}`);
              }
            } else {
              if (!this.isItemFilled(item)) {
                itensPendentesGrupo.push(`${index + 1}. ${item.titulo}`);
              }
            }
          });

          if (itensPendentesGrupo.length > 0) {
            gruposPendentes.push({
              titulo: group.titulo,
              itens: itensPendentesGrupo
            });
          }
        }
      });

      if (totalItens === 0) {
        return { valid: false, message: "Este checklist não possui itens para serem respondidos." };
      }

      if (!this.selectedUnitId) {
        return { valid: false, message: "Selecione a unidade da execução." };
      }

      if (gruposPendentes.length > 0) {
        return {
          valid: false,
          message: "Você não pode salvar com campos vazios.\n\nTodos os campos são obrigatórios para finalizar a checklist."
        };
      }

      return { valid: true, message: "Todos os campos foram preenchidos!" };
    },
  },
};
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.page-header {
  text-align: center;
  margin-bottom: 3rem;
}

.page-title {
  font-size: 2.5rem;
  font-weight: 700;
}

.checklist-container {
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 1rem 3rem;
}

.group-tabs-outer {

  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1.4rem 1rem;
  margin: 0 -1rem 3rem;
  border-radius: 16px;
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  box-shadow: 0 10px 36px rgba(0,0,0,0.28), 0 2px 8px rgba(0,0,0,0.16);
}

body[data-bs-theme="light"] .group-tabs-outer,
.light-layout .group-tabs-outer,
html[data-bs-theme="light"] .group-tabs-outer,
:not([data-bs-theme="dark"]) .group-tabs-outer {
  background: rgba(248, 248, 248, 0.04);
}

body[data-bs-theme="dark"] .group-tabs-outer,
.dark-layout .group-tabs-outer {
  background: rgba(22, 29, 49, 0.04);
}

.tab-arrow {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid var(--bs-border-color);
  background: var(--bs-body-bg);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: inherit;
  transition: background 0.15s, box-shadow 0.15s;
}

.tab-arrow:hover {
  background: rgba(0, 140, 255, 0.1);
  box-shadow: 0 4px 12px rgba(0, 140, 255, 0.25);
  border-color: rgb(0, 140, 255);
  color: rgb(0, 140, 255);
}

.group-tabs-wrapper {
  flex: 1;
  overflow-x: scroll;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}

.group-tabs-wrapper::-webkit-scrollbar {
  display: none;
}

.group-tabs {
  display: flex;
  gap: 1.1rem;
  padding: 0.25rem 0.1rem;
}

.group-tab-btn {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0.65rem 1.2rem;
  border-radius: 12px;
  border: 1.5px solid var(--bs-border-color);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.32);
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, box-shadow 0.15s, transform 0.1s;
  color: inherit;
  flex-shrink: 0;
  max-width: 220px;
}

body[data-bs-theme="light"] .group-tab-btn,
.light-layout .group-tab-btn,
html[data-bs-theme="light"] .group-tab-btn,
:not([data-bs-theme="dark"]) .group-tab-btn {
  background: rgba(255, 255, 255, 0.9);
}

body[data-bs-theme="dark"] .group-tab-btn,
.dark-layout .group-tab-btn {
  background: rgba(22, 29, 49, 0.9);
}


.group-tab-btn:hover {
  border-color: rgb(0, 140, 255);
  background: rgba(0, 140, 255, 0.06);
  box-shadow: 0 4px 14px rgba(0, 140, 255, 0.2);
  transform: translateY(-1px);
}

.group-tab-btn.active {
  border-color: rgb(0, 140, 255);
  background: rgba(0, 140, 255, 0.1);
  box-shadow: none;
  color: rgb(0, 140, 255);
}

.tab-title {
  font-size: 0.95rem;
  font-weight: 600;
  line-height: 1.3;
  white-space: normal;
  overflow-wrap: break-word;
  word-break: break-word;
  max-width: 100%;
}

.tab-progress {
  font-size: 0.75rem;
  opacity: 0.7;
  margin-top: 0.15rem;
}

.group-nav-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1.5rem;
  margin-bottom: 1rem;
}

.btn-nav-grupo {
  padding: 0.6rem 1.5rem;
  border-radius: 999px;
  font-size: 1rem;
}

.group-dark-container {
  margin-bottom: 3rem;
}

/* Com o calendário aberto, o grupo fica por cima da barra de abas */
.group-dark-container:focus-within {
  position: relative;
  z-index: 101;
}

body[data-bs-theme="light"] .group-card,
.light-layout .group-card,
html[data-bs-theme="light"] .group-card,
:not([data-bs-theme="dark"]) .group-card {
  background-color: rgba(235, 235, 235, 0.311) !important;
  border-radius: 16px;
  padding: 2.5rem;
  border: 1px solid var(--bs-border-color);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.24);
}

body[data-bs-theme="dark"] .group-card,
.dark-layout .group-card {
  background-color: rgba(21, 22, 35, 0.566) !important;
  border-radius: 16px;
  padding: 2.5rem;
  border: 1px solid var(--bs-border-color);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.405);
}

.group-header-lined {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 2.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--bs-border-color);
}

.group-title-lined {
  font-size: 1.75rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: rgb(0, 140, 255);
  flex: 1 1 auto;
  min-width: 0;
  overflow-wrap: break-word;
  word-break: break-word;
}

.group-title-line {
  flex: 1;
  height: 6px;
  border-radius: 50px;
  background: linear-gradient(to right,
      rgb(0, 140, 255),
      rgba(0, 0, 0, 0));
}

.group-count {
  font-size: 0.9rem;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  background: var(--bs-secondary-bg);
  color: var(--bs-secondary-color);
}

.item-block {
  margin-bottom: 2.25rem;
  position: relative;
}

.item-label {
  display: flex;
  align-items: flex-start;
  font-size: 1.05rem;
  font-weight: 500;
  margin-bottom: 0.75rem;
}

.item-title-text {
  flex: 1;
  min-width: 0;
  overflow-wrap: break-word;
  word-break: break-word;
}

.item-index {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: var(--bs-border-color);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  font-weight: 700;
  margin-right: 0.85rem;
}

.item-block.filled .item-index {
  background: var(--bs-success);
  color: #fff;
}

.required-star {
  color: #e74c3c;
  font-size: 1rem;
  margin-left: 3px;
  line-height: 1;
}

.input-modern {
  height: 52px;
  font-size: 1rem;
  border-radius: 10px;
  border: none;
  padding: 0 1rem;
}

.input-data {
  display: flex;
  align-items: center;
  padding: 0 0.5rem;
}

.input-data ::v-deep .form-control {
  background: transparent !important;
  border: none;
  color: inherit !important;
  height: auto;
}

.input-modern::placeholder {
  opacity: 0.75;
}

.input-modern:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(var(--bs-primary-rgb), 0.3);
}

body[data-bs-theme="light"] .group-card .input-modern,
.light-layout .group-card .input-modern,
html[data-bs-theme="light"] .group-card .input-modern,
:not([data-bs-theme="dark"]) .group-card .input-modern {
  background: #e5e7eb !important;
  color: #1f2937 !important;
}

body[data-bs-theme="dark"] .group-card .input-modern,
.dark-layout .group-card .input-modern {
  background: #252e378d !important;
  color: #f9fafb !important;
}

.obs-wrapper {
  max-width: 1100px;
  margin: 0 auto 3rem;
}

.informacoes-iniciais-conteudo {
  overflow-wrap: break-word;
  word-break: break-word;
}

.informacoes-iniciais-conteudo::after {
  content: "";
  display: block;
  clear: both;
}

.informacoes-iniciais-conteudo ::v-deep img {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
}

.informacoes-iniciais-conteudo ::v-deep figure.image {
  display: table;
  clear: both;
  text-align: center;
  margin: 1rem auto;
}

.informacoes-iniciais-conteudo ::v-deep figure.image-style-side {
  float: right;
  max-width: 50%;
  margin: 0 0 1rem 1.5rem;
}

.informacoes-iniciais-conteudo ::v-deep figure.image figcaption {
  font-size: 0.85rem;
  color: #6c757d;
  padding-top: 0.4rem;
}

.informacoes-iniciais-conteudo ::v-deep table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 1rem;
}

.informacoes-iniciais-conteudo ::v-deep td,
.informacoes-iniciais-conteudo ::v-deep th {
  border: 1px solid #dee2e6;
  padding: 0.5rem;
}

.informacoes-iniciais-conteudo ::v-deep blockquote {
  border-left: 4px solid rgb(0, 140, 255);
  padding-left: 1rem;
  margin-left: 0;
  font-style: italic;
}

.informacoes-iniciais-conteudo ::v-deep p:last-child {
  margin-bottom: 0;
}

.data-esperada-info {
  display: inline-block;
  font-size: 0.85rem;
  color: rgb(0, 140, 255);
  background: rgba(0, 140, 255, 0.08);
  border-radius: 8px;
  padding: 0.3rem 0.7rem;
  margin-bottom: 0.6rem;
}

body[data-bs-theme="light"] .obs-card,
.light-layout .obs-card,
html[data-bs-theme="light"] .obs-card,
:not([data-bs-theme="dark"]) .obs-card {
  background-color: rgba(235, 235, 235, 0.311) !important;
  border-radius: 16px;
  padding: 2.5rem;
  border: 1px solid var(--bs-border-color);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.24);
}

body[data-bs-theme="dark"] .obs-card,
.dark-layout .obs-card {
  background-color: rgba(21, 22, 35, 0.566) !important;
  border-radius: 16px;
  padding: 2.5rem;
  border: 1px solid var(--bs-border-color);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.405);
}

body[data-bs-theme="light"] .obs-card h4,
.light-layout .obs-card h4,
html[data-bs-theme="light"] .obs-card h4,
:not([data-bs-theme="dark"]) .obs-card h4 {
  text-align: center;
  font-size: 1.60rem;
  margin-bottom: 1.25rem;
  color: #1f2937;
}

body[data-bs-theme="dark"] .obs-card h4,
.dark-layout .obs-card h4 {
  text-align: center;
  font-size: 1.60rem;
  margin-bottom: 1.25rem;
  color: rgba(255, 255, 255, 0.87);
}

body[data-bs-theme="light"] .obs-card .textarea-modern,
.light-layout .obs-card .textarea-modern,
html[data-bs-theme="light"] .obs-card .textarea-modern,
:not([data-bs-theme="dark"]) .obs-card .textarea-modern {
  font-size: 1rem;
  border-radius: 12px;
  background: #e5e7eb !important;
  color: #1f2937 !important;
  border: none;
}

body[data-bs-theme="dark"] .obs-card .textarea-modern,
.dark-layout .obs-card .textarea-modern {
  font-size: 1rem;
  border-radius: 12px;
  background: #252e378d !important;
  color: #f9fafb !important;
  border: none;
}

.action-footer {
  text-align: center;
  margin-bottom: 3rem;
}

.action-footer .btn {
  padding: 0.85rem 2.5rem;
  font-size: 1.1rem;
  border-radius: 999px;
}

.action-footer .btn-secondary {
  background-color: #6c757d;
  border-color: #6c757d;
  color: white;
}

.action-footer .btn-secondary:hover {
  background-color: #5a6268;
  border-color: #545b62;
}

.image-upload-wrapper {
  margin-top: 0.5rem;
}

.btn-upload {
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  font-weight: 500;
  border-radius: 8px;
  transition: all 0.3s ease;
  border: 2px dashed #007bff;
  background-color: transparent;
}

.btn-upload:hover {
  background-color: #007bff;
  border-color: #007bff;
  color: white !important;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 123, 255, 0.3);
}

.file-info {
  display: flex;
  align-items: center;
  padding: 0.5rem;
  background-color: rgba(40, 167, 69, 0.1);
  border: 1px solid rgba(40, 167, 69, 0.3);
  border-radius: 6px;
  font-size: 0.9rem;
}

.file-name {
  color: #28a745;
  font-weight: 500;
  flex: 1;
}

.preview-container {
  text-align: center;
}

.photo {
  max-width: 100%;
  max-height: 200px;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  border: 3px solid #e9ecef;
  cursor: zoom-in;
  transition: transform 0.3s ease;
}

.photo:hover {
  transform: scale(1.05);
}

/* Mobile - Remove linha azul e permite quebra de texto */
@media (max-width: 768px) {
  .group-title-lined {
    white-space: normal;
    word-wrap: break-word;
    overflow-wrap: break-word;
    font-size: 1.3rem;
  }

  .group-title-line {
    display: none;
  }

  .action-footer {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
  }

  .action-footer .btn {
    margin: 0 !important;
    width: 90%;
    max-width: 400px;
  }
}

@media (max-width: 480px) {
  .group-title-lined {
    font-size: 1.1rem;
  }

  .action-footer .btn {
    width: 95%;
    font-size: 1rem;
  }
}
/* Multiple choice */
.multipla-escolha-wrapper {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 4px;
}

.multipla-opcao {
  display: flex;
  flex-direction: column;
}

.multipla-label {
  display: flex;
  align-items: center;
  gap: 0;
  padding: 11px 16px;
  border: 1px solid var(--bs-border-color, #dee2e6);
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s;
  font-size: 1rem;
  font-weight: normal;
  margin-bottom: 0;
}

.opcao-texto {
  margin-left: 14px;
}

.multipla-label:hover {
  background: rgba(0, 123, 255, 0.07);
}

.multipla-label input[type="checkbox"] {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  cursor: pointer;
}

/* Linked conditional items */
.item-vinculado-block {
  position: relative;
  margin-top: 24px;
  padding-left: 28px;
}

/* Vertical + horizontal branch line */
.item-vinculado-block::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 50%;
  width: 2px;
  background: linear-gradient(to bottom, #4f8ef7, #a78bfa);
  border-radius: 2px;
}

.item-vinculado-block::after {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  width: 20px;
  height: 2px;
  background: linear-gradient(to right, #4f8ef7, #a78bfa);
  border-radius: 2px;
}

.item-vinculado-inner {
  margin-bottom: 0;
  border-radius: 12px;
  padding: 18px 20px 14px;
  border: 1.5px solid rgba(99, 102, 241, 0.25);
  background: rgba(99, 102, 241, 0.04);
  box-shadow: 0 2px 12px rgba(99, 102, 241, 0.07);
}

.item-vinculado-label {
  font-size: 1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
  color: inherit;
}

.condicional-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: #6366f1;
  background: rgba(99, 102, 241, 0.1);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 20px;
  padding: 2px 8px;
  margin-bottom: 10px;
  width: fit-content;
}
</style>